import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { networkService } from "../../config/axios";
import CustomTagsInput from "../../CommonComponents/Tags/CustomTagsInput";
import "./LinksPageFilter.css";

const FILTER_FORM_ID = "links-page-filter-form";

const STATUS_OPTIONS = [
    { value: "all", label: "All" },
    { value: "mine", label: "My LaserLINX" },
    { value: "active", label: "Active" },
    { value: "expired", label: "Expired" },
    { value: "password", label: "Password protected" },
    { value: "visited_30d", label: "Visited (past 30 days)" },
    { value: "never_visited", label: "Never visited" },
];
const DEFAULT_STATUS = "all";

const SORT_OPTIONS = [
    { value: "created_desc", label: "Newest first" },
    { value: "created_asc", label: "Oldest first" },
    { value: "name_asc", label: "Title A–Z" },
    { value: "name_desc", label: "Title Z–A" },
    { value: "account_asc", label: "Account name A–Z" },
    { value: "account_desc", label: "Account name Z–A" },
    { value: "visits_desc", label: "Most visits" },
    { value: "last_visit_desc", label: "Recently visited" },
];
const DEFAULT_SORT = "created_desc";

const LinksPageFilter = ({triggerFiltersApplyEvent, initialReceiver, isOpen = true, quickBarTarget = null, ...rest}) => {
    const formRef = useRef(null);
    const [status, setStatus] = useState(DEFAULT_STATUS);
    const [sort, setSort] = useState(DEFAULT_SORT);
    const [domain, setDomain] = useState("");
    const [domainOptions, setDomainOptions] = useState([]);
    const [domainMenuOpen, setDomainMenuOpen] = useState(false);
    const [domainSearch, setDomainSearch] = useState("");
    const domainMenuRef = useRef(null);
    const domainSearchRef = useRef(null);
    const [formData, setFormData] = useState({
        receiver: {name:'', value:[]},
        token: "",
        campaign: {name:'', value:[]},
        laserLinkTags: {name:'', value:[]},
        link: "",
        kdf: "",
        ...rest
    });

    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            receiver: initialReceiver
                ? { name: '', value: [initialReceiver] }
                : { name: '', value: [] }
        }));
    }, [initialReceiver]);

    // Domains of every link this user can see plus user/org assigned domains (see TrackingLinksController#domains).
    useEffect(() => {
        networkService("links/domains.json")
            .then(({ data }) => setDomainOptions(Array.isArray(data?.data) ? data.data : []))
            .catch(() => setDomainOptions([]));
    }, []);

    useEffect(() => {
        if (!domainMenuOpen) return;
        const closeOnOutside = (e) => {
            if (domainMenuRef.current && !domainMenuRef.current.contains(e.target)) setDomainMenuOpen(false);
        };
        document.addEventListener('mousedown', closeOnOutside);
        setTimeout(() => domainSearchRef.current?.focus(), 0);
        return () => document.removeEventListener('mousedown', closeOnOutside);
    }, [domainMenuOpen]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (formRef.current && !formRef.current.contains(e.target)) {
                setApiResponses(prev => ({
                    ...prev, receiver: null, campaign: null, laserLinkTags: null, link: null, kdf: null
                }));
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const [apiResponses, setApiResponses] = useState({
        receiver: null,
        token: null,
        campaign: null,
        laserLinkTags: null,
        link: null,
        kdf: null,
    });

    const [debounceTimers, setDebounceTimers] = useState({
        receiver: null,
        token: null,
        campaign: null,
        laserLinkTags: null,
        link: null,
        kdf: null,
    });
    const handleInputChange = (data, type) => {  
          
        const { name, value } = data;
        
        setFormData((prevData) => {
            let valueToUpdate;
            if(type == 'tagsInput'){
                valueToUpdate = {...prevData[name], 'name':value};
            }else if(type == 'tagsInputRemove'){
                valueToUpdate = {...prevData[name], 'value':value, 'name':''};
            }else if(type == 'tagsValue'){
                valueToUpdate = {...prevData[name], 'value':[...prevData[name].value, value], 'name':''};
            }else if(name == 'kdf'){
                valueToUpdate = {...prevData[name], 'value': (type == 'input' ? value : value?.title), 'tl_id':(type == 'input' ? '' : value?.tracking_link?.id ?? '')}
            }else {
                valueToUpdate = value;
            }
            return {
                ...prevData,
                [name]: valueToUpdate,
            }
        });
        if(type == 'tagsValue' || type == 'tagsInputRemove' || (name == 'kdf' && (type != 'input' || value == ''))){
            applyOnChangeRef.current = true;
        }
        if(name == 'token' || name == 'link'){
            clearTimeout(textApplyTimerRef.current);
            textApplyTimerRef.current = setTimeout(() => submitRef.current(), 600);
        }
        if(type == 'tagsInput'){
            debounceApiCall(name, value);
        }else if(type == 'input'){
            debounceApiCall(name, value);
        }
    };
    const debounceApiCall = (inputName, inputValue) => {
        if (debounceTimers[inputName]) {
            clearTimeout(debounceTimers[inputName]);
        }
        const newTimeout = setTimeout(() => {
            fetchApiResponse(inputName, inputValue);
        }, 1000);
        setDebounceTimers((prevTimers) => ({
            ...prevTimers,
            [inputName]: newTimeout,
        }));
    };
    const fetchApiResponse = async (inputName, inputValue) => {
        
        if (!inputValue) {
            setApiResponses((prev) => ({
                ...prev,
                [inputName]: null,
            }));
            return;
        }
        try {
            let payloadURL = '';
            if(inputName == 'campaign'){
                payloadURL = `campaigns.json?campaign_name=${encodeURIComponent(inputValue)}`;
            }else if(inputName == 'link'){
                payloadURL = `link.json?search=true&name=${encodeURIComponent(inputValue)}`;
            }else if(inputName == 'kdf'){
                payloadURL = `kdfs.json?title=${encodeURIComponent(inputValue)}&tl=yes`;
            }
            else if(inputName == 'laserLinkTags'){
                payloadURL = `tags.json?tag_name=${encodeURIComponent(inputValue)}&items=link_list&type=tl`;
            }else if(inputName == 'receiver'){
                payloadURL = `receivers.json?receiver=${encodeURIComponent(inputValue)}`;
            }
            // else {
            //     payloadURL = `${inputName}=${encodeURIComponent(inputValue)}`;
            // }
            const { data, status } = await networkService(payloadURL);
            // const { data, status } = await networkService(`${inputName?.includes('laserLinkTags') ? 'laserLinkTag' : inputName+'s'}.json?${payloadURL}`);            
            
            setApiResponses((prev) => ({
                ...prev,
                [inputName]: data,
            }));
            
        } catch (error) {
            // console.error("Error fetching data:", error);
            setApiResponses((prev) => ({
                ...prev,
                [inputName]: ["Error fetching results"],
            }));
        }
    };

    const handleSubmit = (e, overrides = {}) => {
        e?.preventDefault();
        let formPayload = {};
        const effectiveStatus = overrides.status ?? status;
        const effectiveSort = overrides.sort ?? sort;
        const effectiveDomain = overrides.domain ?? domain;
        if (effectiveStatus !== DEFAULT_STATUS) formPayload['status'] = effectiveStatus;
        if (effectiveSort !== DEFAULT_SORT) formPayload['sort'] = effectiveSort;
        if (effectiveDomain) formPayload['domain'] = effectiveDomain;
        
        if(formData?.kdf?.value){
            formPayload['doc_name'] = formData?.kdf?.value;
        }
        if(formData?.link != ''){
            formPayload['name'] = formData?.link;
        }
        if(formData?.token != ''){
            formPayload['token'] = formData?.token;
        }
        if(formData?.campaign?.value?.length > 0){
            formPayload['campaigns'] = formData?.campaign?.value?.map(it => it.id);
        }
        if(formData?.laserLinkTags?.value?.length > 0){
            formPayload['tl_ids'] = formData?.laserLinkTags?.value?.flatMap(it => it.tl_ids || []);
        }
        if(formData?.receiver?.value?.length > 0){
            formPayload['receiver_tl_ids'] = formData?.receiver?.value?.flatMap(it => it.tl_ids || []);
        }
        if(formData?.receiver?.name?.trim()?.length >= 3){
            formPayload['recipient_info'] = formData?.receiver?.name?.trim();
        }
        const filterCount = [
            effectiveStatus !== DEFAULT_STATUS,
            formPayload['doc_name'],
            formPayload['name'],
            formPayload['token'],
            formPayload['campaigns'],
            formPayload['tl_ids'],
            formPayload['receiver_tl_ids'] || formPayload['recipient_info'],
        ].filter(Boolean).length;
        triggerFiltersApplyEvent(formPayload, filterCount);
    };

    const submitRef = useRef(null);
    submitRef.current = handleSubmit;
    const textApplyTimerRef = useRef(null);
    useEffect(() => () => clearTimeout(textApplyTimerRef.current), []);

    const applyOnChangeRef = useRef(false);
    useEffect(() => {
        if (!applyOnChangeRef.current) return;
        applyOnChangeRef.current = false;
        handleSubmit();
    }, [formData]);

    const handleReset = () => {
        clearTimeout(textApplyTimerRef.current);
        setStatus(DEFAULT_STATUS);
        setSort(DEFAULT_SORT);
        setDomain("");
        setDomainSearch("");
        setDomainMenuOpen(false);
        setFormData({
            receiver: {name:'', value:[]},
            token: "",
            campaign: {name:'', value:[]},
            laserLinkTags: {name:'', value:[]},
            link: "",
            kdf: "",
        });
        setApiResponses({
            receiver: null,
            token: null,
            campaign: null,
            laserLinkTags: null,
            link: null,
            kdf: null,
        });
        // Reset filters - pass empty object to clear all filters
        triggerFiltersApplyEvent({})
    };

    const handleStatusChange = (value) => {
        setStatus(value);
        handleSubmit(undefined, { status: value });
    };

    const handleSortChange = (value) => {
        setSort(value);
        handleSubmit(undefined, { sort: value });
    };

    const handleDomainChange = (value) => {
        setDomain(value);
        setDomainMenuOpen(false);
        setDomainSearch("");
        handleSubmit(undefined, { domain: value });
    };

    const filteredDomains = domainOptions.filter((d) => d.includes(domainSearch.trim().toLowerCase()));

    const renderDomainDropdown = () => (
        <div className="all-btn links-domain-filter" ref={domainMenuRef}>
            <div className="dropdown">
                <a
                    className="btn btn-secondary dropdown-toggle"
                    href="#"
                    role="button"
                    aria-expanded={domainMenuOpen}
                    aria-label="Filter LaserLINX by domain"
                    title={domain || undefined}
                    onClick={(e) => { e.preventDefault(); setDomainMenuOpen((open) => !open); }}
                >
                    <span className="links-domain-filter__label">{domain || "All domains"}</span>
                </a>
                {domainMenuOpen && (
                    <div className="dropdown-menu show links-domain-filter__menu">
                        <input
                            ref={domainSearchRef}
                            type="text"
                            className="links-domain-filter__search"
                            placeholder="Search domains"
                            value={domainSearch}
                            onChange={(e) => setDomainSearch(e.target.value)}
                            onKeyDown={(e) => {
                                // Enter picks the top match instead of submitting the whole filter form.
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (filteredDomains.length > 0) handleDomainChange(filteredDomains[0]);
                                } else if (e.key === "Escape") {
                                    setDomainMenuOpen(false);
                                }
                            }}
                        />
                        <ul className="links-domain-filter__list">
                            {!domainSearch.trim() && (
                                <li
                                    className={`dropdown-item${!domain ? " active" : ""}`}
                                    onClick={() => handleDomainChange("")}
                                >
                                    All domains
                                </li>
                            )}
                            {filteredDomains.map((d) => (
                                <li
                                    key={d}
                                    className={`dropdown-item${d === domain ? " active" : ""}`}
                                    onClick={() => handleDomainChange(d)}
                                >
                                    {d}
                                </li>
                            ))}
                            {filteredDomains.length === 0 && (
                                <li className="dropdown-item text-muted links-domain-filter__empty">No domains found</li>
                            )}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );

    const renderStatusDropdown = () => (
        <div className="all-btn">
            <div className="dropdown">
                <a
                    className="btn btn-secondary dropdown-toggle"
                    href="#"
                    role="button"
                    id="linksStatusMenuLink"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    aria-label="Filter LaserLINX"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                        <path fill="#294561" d="M10 18v-2h4v2zm-4-5v-2h12v2zM3 8V6h18v2z"></path>
                    </svg>
                    &nbsp;
                    {STATUS_OPTIONS.find((o) => o.value === status)?.label}
                </a>
                <ul className="dropdown-menu" aria-labelledby="linksStatusMenuLink">
                    {STATUS_OPTIONS.map((option) => (
                        <li
                            key={option.value}
                            className={`dropdown-item${option.value === status ? " active" : ""}`}
                            onClick={() => handleStatusChange(option.value)}
                        >
                            {option.label}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );

    const renderSortDropdown = () => (
        <div className="all-btn">
            <div className="dropdown">
                <a
                    className="btn btn-secondary dropdown-toggle"
                    href="#"
                    role="button"
                    id="linksSortMenuLink"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    aria-label="Sort LaserLINX"
                >
                    {SORT_OPTIONS.find((o) => o.value === sort)?.label}
                </a>
                <ul className="dropdown-menu" aria-labelledby="linksSortMenuLink">
                    {SORT_OPTIONS.map((option) => (
                        <li
                            key={option.value}
                            className={`dropdown-item${option.value === sort ? " active" : ""}`}
                            onClick={() => handleSortChange(option.value)}
                        >
                            {option.label}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );

    const renderTitleInput = () => (
        <div className="d-flex form-group position-relative filter-title-input-group">
            <input
                className="kdf-Tags2 ms-0"
                type="text"
                placeholder="LaserLINK Title"
                name="link"
                value={formData.link}
                onChange={(value) => handleInputChange(value.target)}
            />
        </div>
    );

    const renderActionButtons = () => (
        <div className="compact-filter-actions">
            <button type="button" className="reset-btn" onClick={handleReset}>
             <img src="/icons/autorenew.svg"/>   Reset
            </button>
        </div>
    );

    return (
        <>
        <form ref={formRef} id={FILTER_FORM_ID} onSubmit={handleSubmit} className="filterForm links-page-filter">
            {/* {JSON.stringify(formData)} */}
            {isOpen && (
                <div className="filter-primary-row">
                    {renderTitleInput()}
                    {renderActionButtons()}
                </div>
            )}
            <div className="filter-refine-row">
                {isOpen && <div className="filter-field"><span className="filter-field-label">Status</span>{renderStatusDropdown()}</div>}
                {isOpen && <div className="filter-field"><span className="filter-field-label">Sort</span>{renderSortDropdown()}</div>}
                {isOpen && <div className="filter-field"><span className="filter-field-label">Domain</span>{renderDomainDropdown()}</div>}
                <div className="filter-field"><span className="filter-field-label">Recipients</span>
                <div className="form-group position-relative custom-tags">
                    {/* <input
                        className="kdf-Tags3 ms-0"
                        type="text"
                        placeholder="Recipients..."
                        name="receiver"
                        value={formData.receiver}
                        onChange={(value) => handleInputChange({name:'receiver',"value":value})}
                    /> */}
                    <CustomTagsInput
                        placeholderText={'Recipients...'}
                        tags={formData.receiver.value || []}
                        targetKey="name"
                        handleRemoveTags={(value) => handleInputChange({name:'receiver',"value":value}, 'tagsInputRemove')}
                        autoSubmit={true}
                        collapsedWhenHasTags={false}
                        triggerKeyDownEvent={(value) => handleInputChange({name:'receiver',"value":value}, 'tagsInput')}
                    />
                    {apiResponses.receiver !== null && (
                        <div className="api-response">
                            <ul className="kdf-title-drop-ul">
                                {apiResponses.receiver?.length === 0 ? (
                                    <li className="kdf-title-drop-li text-muted" style={{cursor:'default'}}>No results found</li>
                                ) : apiResponses.receiver?.map((result, index) =>
                                    formData?.receiver?.value?.filter(it => it.id == result?.id)?.length == 0 &&
                                    <li
                                        key={index}
                                        className='cursor-pointer kdf-title-drop-li'
                                        onClick={() => (handleInputChange({name:'receiver',"value":result}, 'tagsValue'), setApiResponses(prev => ({...prev, "receiver": null})))}
                                    >
                                        <span>{result?.name}</span>
                                        {result?.email && <small className="text-muted">{result?.email}</small>}
                                    </li>
                                )}
                            </ul>
                        </div>
                    )}
                </div>
                </div>
                <div className="filter-field"><span className="filter-field-label">LaserLINK URL</span>
                <div className="d-flex form-group">
                    <input
                        className="kdf-Tags3"
                        type="text"
                        placeholder="LaserLINK URL"
                        name="token"
                        value={formData.token}
                        onChange={(value) => handleInputChange(value.target)}
                    />
                </div>
                </div>
                <div className="filter-field"><span className="filter-field-label">Campaigns</span>
                <div className="d-flex form-group position-relative custom-tags">
                    {/* <input
                        className="kdf-Tags3"
                        type="text"
                        placeholder="Campaigns"
                        name="campaign"
                        value={formData.campaign}
                        onChange={(value) => handleInputChange({name:'campaign',"value":value})}
                    /> */}
                    {/* {JSON.stringify(formData?.campaign?.value)} */}
                    <CustomTagsInput
                        placeholderText={'Campaigns'}
                        tags={formData?.campaign?.value || []}
                        handleRemoveTags={(value) => handleInputChange({name:'campaign',"value":value}, 'tagsInputRemove')}
                        autoSubmit={true}
                        collapsedWhenHasTags={false}
                        triggerKeyDownEvent={(value) => handleInputChange({name:'campaign',"value":value}, 'tagsInput')}
                    />
                    {apiResponses?.campaign !== null && (
                        <div className="api-response">
                            <ul className="kdf-title-drop-ul">
                                {apiResponses?.campaign?.length === 0 ? (
                                    <li className="kdf-title-drop-li text-muted" style={{cursor:'default'}}>No results found</li>
                                ) : apiResponses?.campaign?.map((result, index) =>
                                    formData?.campaign?.value?.filter(it => it.name == result?.name)?.length == 0 &&
                                    <li
                                        key={index}
                                        className='cursor-pointer kdf-title-drop-li'
                                        onClick={() => (handleInputChange({name:'campaign',"value":result}, 'tagsValue'), setApiResponses(prev => ({...prev, "campaign": null})))}
                                    >{result?.name}</li>
                                )}
                            </ul>
                        </div>
                    )}
                </div>
                </div>
                <div className="filter-field"><span className="filter-field-label">Tags</span>
                <div className="d-flex form-group  laserlink-inpp">
                    {/* <input
                        className="kdf-Tags2"
                        type="text"
                        placeholder="LaserLINK Tags"
                        name="laserLinkTags"
                        value={formData.laserLinkTags}
                        onChange={(value) => handleInputChange({name:'laserLinkTags',"value":value})}
                    /> */}

                    <CustomTagsInput
                        placeholderText={'LaserLINK Tags'}
                        tags={formData.laserLinkTags.value || []}
                        handleRemoveTags={(value) => handleInputChange({name:'laserLinkTags',"value":value}, 'tagsInputRemove')}
                        autoSubmit={true}
                        collapsedWhenHasTags={false}
                        triggerKeyDownEvent={(value) => handleInputChange({name:'laserLinkTags',"value":value}, 'tagsInput')}
                    />

                    {apiResponses?.laserLinkTags !== null && (
                        <div className="api-response">
                            <ul className="kdf-title-drop-ul">
                                {apiResponses?.laserLinkTags?.length === 0 ? (
                                    <li className="kdf-title-drop-li text-muted" style={{cursor:'default'}}>No results found</li>
                                ) : apiResponses?.laserLinkTags?.map((result, index) =>
                                    formData?.laserLinkTags?.value?.filter(it => it.name == result?.name)?.length == 0 &&
                                    <li
                                        key={index}
                                        className='cursor-pointer kdf-title-drop-li'
                                        onClick={() => (handleInputChange({name:'laserLinkTags',"value":result}, 'tagsValue'), setApiResponses(prev => ({...prev, "laserLinkTags": null})))}
                                    >{result?.name}</li>
                                )}
                            </ul>
                        </div>
                    )}
                </div>
                </div>
                <div className="filter-field"><span className="filter-field-label">KDF</span>
                <div className="d-flex form-group position-relative">
                    <input
                        className="kdf-Tags2"
                        type="text"
                        placeholder="KDF Title"
                        name="kdf"
                        value={formData?.kdf?.value}
                        onChange={(e) => handleInputChange(e.target, 'input')}
                    />
                    {/* {JSON.stringify(apiResponses?.kdf)} */}
                    {apiResponses?.kdf && (
                        <div className="api-response">
                            <ul className="kdf-title-drop-ul">
                                {apiResponses?.kdf.map((result, index) => (
                                    <li
                                        key={index}
                                        className='cursor-pointer kdf-title-drop-li'
                                        onClick={() => (handleInputChange({'name':'kdf',"value":result}), setApiResponses(prev => ({...prev, "kdf": null})))}
                                    >
                                        {result?.title}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                </div>
            </div>
        </form>
        {!isOpen && quickBarTarget && ReactDOM.createPortal(
            <>
                {renderTitleInput()}
                {renderActionButtons()}
            </>,
            quickBarTarget
        )}
        </>
    );
};

export default LinksPageFilter;
